/**
 * Helper for implementing delivery date picker
 */
(function (deliveryDateUtil) {
  const DELIVERY_DAYS_BEFORE_EVENT_LIMIT = 5;
  deliveryDateUtil.DELIVERY_DAYS_BEFORE_EVENT_LIMIT =
    DELIVERY_DAYS_BEFORE_EVENT_LIMIT;

  /**
   * Validate a date with the following criteria
   *  - not within unavailable date ranges
   *  - within minimum and maximum date
   */
  const validateDate = (date, deliveryDateInfo) => {
    if (!deliveryDateInfo || !deliveryDateInfo.availabilities) {
      return false;
    }

    const { full, merged } = deliveryDateInfo.availabilities;

    // Format the date for comparison
    const dateStr = formatDateForComparison(date);

    // Check if date is in any of the unavailable ranges
    const isUnavailable = full.some((range) => {
      const fromDate = range.from;
      const toDate = range.to;
      const isInRange = dateStr >= fromDate && dateStr <= toDate;
      return isInRange;
    });

    // Get minimum and maximum dates
    const minDate = getMinDate(deliveryDateInfo);
    const maxDate = getMaxDateFromMinDate(minDate);
    const isWithinMinMax = date >= minDate && date <= maxDate;

    return !isUnavailable && isWithinMinMax;
  };

  /**
   * Format a date as MM-DD-YYYY for comparison
   */
  const formatDateForComparison = (date) => {
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  };

  deliveryDateUtil.dateFormat = (inputDate, format) => {
    //parse the input date
    const date = new Date(inputDate);

    //extract the parts of the date
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();

    //replace the month
    format = format.replace("MM", month.toString().padStart(2, "0"));

    //replace the year
    if (format.indexOf("yyyy") > -1) {
      format = format.replace("yyyy", year.toString());
    } else if (format.indexOf("yy") > -1) {
      format = format.replace("yy", year.toString().substr(2, 2));
    }

    //replace the day
    format = format.replace("dd", day.toString().padStart(2, "0"));

    return format;
  };

  deliveryDateUtil.validateDate = validateDate;

  const isSaturdayDate = (date) => date.getDay() === 6;
  deliveryDateUtil.isSaturdayDate = isSaturdayDate;

  const validateWithinRange = (date, ranges) => {
    for (range of ranges) {
      const fromDate = new Date(new Date(range[0]).setHours(24, 0, 0, 0));
      const toDate = new Date(new Date(range[1]).setHours(24, 0, 0, 0));
      const compareDate = new Date(date.setHours(0, 0, 0, 0));

      if (compareDate >= fromDate && compareDate <= toDate) {
        return true;
      }
    }
    return false;
  };

  const addDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };
  deliveryDateUtil.addDays = addDays;

  const subtractDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() - days);
    return result;
  };
  deliveryDateUtil.subtractDays = subtractDays;

  const getMinDate = (deliveryDateInfo) => {
    if (!deliveryDateInfo || !deliveryDateInfo.availabilities) {
      return new Date();
    }

    // Find processing time from messages
    const processingTimeRange = deliveryDateInfo.availabilities.full.find(
      (range) =>
        range.message &&
        range.message.includes("product needs") &&
        range.message.includes("days to deliver")
    );

    let processingDays = 7; // Default processing time
    if (processingTimeRange) {
      const match = processingTimeRange.message.match(/needs (\d+) days/);
      if (match && match[1]) {
        processingDays = parseInt(match[1], 10);
      }
    }

    // Calculate minimum date based on processing time
    const minDate = addDays(new Date(), processingDays);
    return minDate;
  };

  const getMaxDateFromMinDate = (minDate) => {
    if (!minDate) return;
    const maxDate = new Date(
      minDate.getFullYear() + 2,
      minDate.getMonth(),
      minDate.getDate()
    );
    return maxDate;
  };

  async function getAuthToken() {
    const base64 = btoa(
      `${window.username_data_endpoin_user}:${window.password_data_endpoint}`
    );
    const options = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Basic ${base64}`,
      },
    };

    const rs = await fetch(`${window.url_data_endpoint_auth}`, options);
    if (rs.status === 200) {
      const res = await rs.json();
      return res.data.token;
    }

    console.error("Failed to get auth token");
    return undefined;
  }

  const getDeliveryDateInfo = async (productIds) => {
    const token = await getAuthToken();
    if (!token) {
      console.error("No auth token available");
      return { availabilities: { full: [], merged: [] } };
    }

    const hmac = window.hmac_key || "";
    const date = new Date();
    const year = date.getFullYear();
    const month = date.getMonth() + 1; // JavaScript months are 0-indexed

    const payload = {
      product_ids: Array.isArray(productIds) ? productIds : [productIds],
      year: year,
      month: month,
      tz: "UTC",
      from: "delivery-date-utils",
    };

    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "x-shopify-hmac-sha256": hmac,
      },
      body: JSON.stringify(payload),
    };

    const url = `${window.product_data_endpoint}/bundles/calendar`;
    const response = await fetch(url, options);
    if (!response.ok) {
      console.error(
        `Failed to fetch calendar data: ${response.status} ${response.statusText}`
      );
      return { availabilities: { full: [], merged: [] } };
    }

    const data = await response.json();
    return data;
  };
  deliveryDateUtil.getDeliveryDateInfo = getDeliveryDateInfo;
})((window.deliveryDateUtil = window.deliveryDateUtil || {}));
